import React from 'react';
import { withRouter } from 'react-router';
import UnsavedChangesTrackingPageComponent from 'components/UnsavedChangesTrackingPageComponent';
import UnsavedChangesWarning from 'components/UnsavedChangesWarning';
import { bindActionCreators } from 'redux';
import { push } from 'react-router-redux';
import { List } from 'immutable';
import { connect, Grid, ApiCall, PrivilegeChecker, ConfirmDialog } from 'omni-common-ui';
import { actions as groupActions } from 'containers/Group';
import {
  actions as individualDevelopmentPlans,
  accessors as idpAccessors,
} from 'data/IndividualDevelopmentPlans';
import StudentInformation from './StudentInformation';
import FeedbackSuggestions from './FeedbackSuggestions';
import ReportCard from './ReportCard';

import StudyPlan from './StudyPlan';
import OverallDevelopment from './OverallDevelopment';
import { Modes } from './routes';
import ExportDialog from './ExportDialog';
import Privileges from 'domain/Privileges';
import RejectNote from 'components/RejectNote';
import { IdpStatus } from 'components/IndividualDevelopmentPlanStatus';
import PropTypes from 'prop-types';

class IndividualDevelopmentPlanDetailView extends UnsavedChangesTrackingPageComponent {
  constructor(props) {
    super(props);
    this.state = {
      isExpandStudentInfo: false,
      isExpandReportCard: false,
    };

    this._changeMode = this._changeMode.bind(this);
  }

  componentDidMount() {
    this._fetchIndividualDevelopmentPlanDetail();
  }

  componentWillReceiveProps(nextProps) {
    this._fetchIndividualDevelopmentPlanDetail(nextProps);
  }

  routerWillLeave(nextLocation) {
    if (nextLocation && nextLocation.pathname.endsWith(`/${Modes.SUBMIT}`)) {
      this._onSubmit();
      return false;
    }

    return super.routerWillLeave(nextLocation);
  }

  _onSubmit() {
    const { idpId, groupIdpId } = this.props;
    this.props.submitIndividualDevelopmentPlan(idpId, groupIdpId)
      .then(this._changeMode());
  }

  _isChangesPresent() {
    if (IndividualDevelopmentPlanDetailView.isInEditCommentsMode(this.props)) {
      return this.feedbackSuggestionsComponent.wasCommentsEditMade();
    }
    if (IndividualDevelopmentPlanDetailView.isInEditStudyPlanMode(this.props)) {
      return this.studyPlanComponent.wasEditMade();
    }
    if (IndividualDevelopmentPlanDetailView.isInEditOverallDevelopmentMode(this.props)) {
      return this.overallDevelopmentComponent.wasEditMade();
    }
    return false;
  }

  _fetchIndividualDevelopmentPlanDetail(props = this.props) {
    if (! props.idpId) return;
    props.fetchIdpLearningResultIfNeeded(props.idpId);
    props.fetchIdpFeedbackSuggestionsIfNeeded(props.idpId);
    props.fetchIdpParticipationIfNeeded();
  }

  _expandStudentInfo() {
    this.setState({ isExpandStudentInfo: ! this.state.isExpandStudentInfo });
  }

  _expandReportCard() {
    this.setState({ isExpandReportCard: ! this.state.isExpandReportCard });
  }

  _renderStudentInformation() {
    if (! this.props.studentInfo || ! this.props.timeZone) return null;
    return <StudentInformation studentInfo={this.props.studentInfo}
        isExpanded={this.state.isExpandStudentInfo}
        onExpandButtonClick={() => this._expandStudentInfo()}
        idpId={this.props.idpId}
        groupIdpId={this.props.groupIdpId}
        groupStartDate={this.props.groupStartDate}
        groupEndDate={this.props.groupEndDate}
        timeZone={this.props.timeZone}
        isInEditMode={IndividualDevelopmentPlanDetailView.isInEditMode(this.props)}
        canEdit={this.props.canEdit} />;
  }

  _renderReportCard() {
    if (! this.props.reportCardInfo) return null;
    const shouldShowAttendance = this.props.isFromOdin;
    return <ReportCard reportCardInfo={this.props.reportCardInfo}
        isExpanded={this.state.isExpandReportCard}
        onExpandButtonClick={() => this._expandReportCard()}
        shouldShowAttendance={shouldShowAttendance} />;
  }

  _renderSuggestions() {
    if (! this.props.suggestionsInfo) return null;
    return <FeedbackSuggestions onComponentMounted={(_cmpt) => {
      this.feedbackSuggestionsComponent = _cmpt;
    }}
        suggestionsInfo={this.props.suggestionsInfo}
        participationInfo={this.props.idpParticipation}
        isSSV3Group={this.props.isSSV3Group}
        isSkillSuggestionManualOnly={this.props.isSkillSuggestionManualOnly}
        isInEditMode={IndividualDevelopmentPlanDetailView.isInEditCommentsMode(this.props)}
        changeMode={this._changeMode}
        idpId={this.props.idpId}
        groupIdpId={this.props.groupIdpId}
        canEdit={this.props.canEdit}
        shouldShowSuggestions={this.props.shouldShowSuggestions}
        skillRecommendations={this.props.skillRecommendations} />;
  }

  _renderOverallDevelopment() {
    if (! this.props.isSSV3Group || ! this.props.studentInfo) return null;
    return <OverallDevelopment onComponentMounted={(_cmpt) => {
      this.overallDevelopmentComponent = _cmpt;
    }}
        studentInfo={this.props.studentInfo}
        idpId={this.props.idpId}
        groupIdpId={this.props.groupIdpId}
        canEdit={this.props.canEdit}
        changeMode={this._changeMode}
        isInEditMode={
          IndividualDevelopmentPlanDetailView.isInEditOverallDevelopmentMode(this.props)} />;
  }

  _renderStudyPlan() {
    if (! this.props.studentInfo) return null;
    return <StudyPlan onComponentMounted={(_cmpt) => {
      this.studyPlanComponent = _cmpt;
    }}
        groupId={this.props.groupId}
        studentInfo={this.props.studentInfo}
        idpId={this.props.idpId}
        groupIdpId={this.props.groupIdpId}
        canEdit={this.props.canEdit}
        changeMode={this._changeMode}
        isInEditMode={IndividualDevelopmentPlanDetailView.isInEditStudyPlanMode(this.props)} />;
  }

  _changeMode(mode = '') {
    const { buildRoute } = this.props;
    this._forcePushRoute(buildRoute({ mode }));
  }

  _onDiscardClick() {
    this.feedbackSuggestionsComponent.setWasCommentsEditMade(false);
    this.studyPlanComponent.setWasEditMade(false);
    this.overallDevelopmentComponent.setWasEditMade(false);
  }

  _shouldRenderStayLeaveWarning() {
    if (IndividualDevelopmentPlanDetailView.isInEditCommentsMode(this.props)) {
      return false;
    }
    if (IndividualDevelopmentPlanDetailView.isInEditStudyPlanMode(this.props)) {
      return this.studyPlanComponent &&
          this.studyPlanComponent.shouldDisableSaveButton();
    }
    if (IndividualDevelopmentPlanDetailView.isInEditOverallDevelopmentMode(this.props)) {
      return this.overallDevelopmentComponent &&
          this.overallDevelopmentComponent.shouldDisableSaveButton();
    }
    return false;
  }

  _onSaveClickedOnWarningDialog() {
    if (IndividualDevelopmentPlanDetailView.isInEditCommentsMode(this.props)) {
      this.feedbackSuggestionsComponent.onSaveClick();
    }
    if (IndividualDevelopmentPlanDetailView.isInEditStudyPlanMode(this.props)) {
      this.studyPlanComponent.onSaveClick();
    }
    if (IndividualDevelopmentPlanDetailView.isInEditOverallDevelopmentMode(this.props)) {
      this.overallDevelopmentComponent.onSaveClick();
    }
  }

  _renderUnsavedChangesWarningDialog() {
    if (this._shouldRenderStayLeaveWarning()) {
      return <ConfirmDialog isOpen={!! this._isNavigationWithChangesRequested()}
          primaryButtonContent="Stay"
          secondaryButtonContent="Leave"
          content="Are you sure you want to leave this page and lose unsaved changes?"
          onPrimaryClick={() => {
            this._clearRequestedLocation();
          }}
          onSecondaryClick={() => {
            this._onDiscardClick();
            this._forcePushRequestedLocation();
          }}
          onRequestClose={() => {
            this._clearRequestedLocation();
          }} />;
    }
    return <UnsavedChangesWarning isOpen={
          !! this._isNavigationWithChangesRequested()}
        isSaving={this.props.isAnyApiCallSaving}
        onSaveClicked={() => {
          this._onSaveClickedOnWarningDialog();
        }}
        onDiscardClicked={() => {
          this._onDiscardClick();
          this._forcePushRequestedLocation();
        }}
        onCloseClicked={() => {
          this._clearRequestedLocation();
        }}
        onSaved={() => {
          this._forcePushRequestedLocation();
        }} />;
  }

  _renderExportDialog() {
    const {
      mode,
      buildRoute,
      pushRoute,
      languageCode,
      idpId,
      reportName,
    } = this.props;
    const isInExportRoute = mode === Modes.EXPORT;
    return isInExportRoute &&
      <ExportDialog idpId={idpId}
          languageCode={languageCode}
          pushRoute={pushRoute}
          buildRoute={buildRoute}
          reportName={reportName} />;
  }

  _renderRjectNote() {
    const { idpStatus = '', dosRejectedComment, timeZone } = this.props;
    return dosRejectedComment &&
      idpStatus.toLowerCase() === IdpStatus.REJECTED.toLowerCase() &&
      <RejectNote note={dosRejectedComment.rejectedComment}
          rejectTime={dosRejectedComment.rejectedDate}
          timeZone={timeZone}
          teacherId={dosRejectedComment.rejectedBy} />;
  }

  render() {
    return <Grid outerMargin>
      <Grid.Group>
        {this._renderRjectNote()}
        {this._renderStudentInformation()}
        {this._renderReportCard()}
        {this._renderSuggestions()}
        {this._renderOverallDevelopment()}
        {this._renderStudyPlan()}
        {this._renderUnsavedChangesWarningDialog()}
        {this._renderExportDialog()}
      </Grid.Group>
    </Grid>;
  }
}

IndividualDevelopmentPlanDetailView.isInEditCommentsMode = (props) =>
  props.params && props.params.mode === Modes.EDIT_COMMENTS;

IndividualDevelopmentPlanDetailView.isInEditStudyPlanMode = (props) =>
  props.params && props.params.mode === Modes.EDIT_STUDY_PLAN;

IndividualDevelopmentPlanDetailView.isInEditOverallDevelopmentMode = (props) =>
  props.params && props.params.mode === Modes.EDIT_OVERALL_DEVELOPMENT;

IndividualDevelopmentPlanDetailView.isInEditMode = (props) =>
  IndividualDevelopmentPlanDetailView.isInEditCommentsMode(props) ||
  IndividualDevelopmentPlanDetailView.isInEditStudyPlanMode(props) ||
  IndividualDevelopmentPlanDetailView.isInEditOverallDevelopmentMode(props);

IndividualDevelopmentPlanDetailView.propTypes = {
  fetchIdpLearningResultIfNeeded: PropTypes.func.isRequired,
  fetchIdpFeedbackSuggestionsIfNeeded: PropTypes.func.isRequired,
  fetchIdpParticipationIfNeeded: PropTypes.func.isRequired,
  submitIndividualDevelopmentPlan: PropTypes.func.isRequired,
  idpParticipation: PropTypes.shape(),
  studentInfo: PropTypes.shape(),
  reportCardInfo: PropTypes.shape(),
  suggestionsInfo: PropTypes.shape(),
  skillRecommendations: PropTypes.shape(),
  dosRejectedComment: PropTypes.shape(),
  idpId: PropTypes.string,
  groupIdpId: PropTypes.string,
  groupId: PropTypes.string,
  isFromOdin: PropTypes.bool,
  canEdit: PropTypes.bool,
  isSSV3Group: PropTypes.bool,
  isSkillSuggestionManualOnly: PropTypes.bool,
  shouldShowSuggestions: PropTypes.bool,
  buildRoute: PropTypes.func.isRequired,
  pushRoute: PropTypes.func,
  isAnyApiCallSaving: PropTypes.bool,
  groupStartDate: PropTypes.shape(),
  groupEndDate: PropTypes.shape(),
  timeZone: PropTypes.string,
  mode: PropTypes.string,
  languageCode: PropTypes.string,
  reportName: PropTypes.string,
};

function mapStateToProps(state, ownProps) {
  const { groupId, groupIdpId, studentId, mode } = ownProps.params;
  const { timeZone } = groupActions.getCentre(state, groupId);
  const idpParticipation = state.get('rootReducer').get('idpParticipation');
  const groupIdpList = state.get('rootReducer').get('individualDevelopmentPlanList');
  const shouldShowSuggestions =
    idpAccessors.shouldShowSuggestions(groupIdpList, groupIdpId, studentId);
  const studentInfo = idpAccessors.getStudentInfo(state, ownProps);
  const reportCardInfo = idpAccessors.getReportCardInfo(state, ownProps);
  const idpId = idpAccessors.getIdpId(state, groupIdpId, studentId);
  const idpStatus = idpAccessors.getIdpStatus(state, idpId);
  const dosRejectedComment = idpAccessors.getDosRejectedComment(state, ownProps);
  const suggestionsInfo = idpAccessors.getSuggestionsInfo(state, ownProps);
  const group = state.get('rootReducer').get('groups').get(groupId).data;
  const isFromOdin = group.isFromOdin;
  const languageCode = group.get('languageCode');
  const skillRecommendations = state.get('rootReducer').get('skillRecommendation').get(groupId);
  const { start: groupStartDate, end: groupEndDate } = group.period;

  const canEdit = PrivilegeChecker.hasPrivilege(
      state, Privileges.IndividualDevelopmentPlan.Manage) &&
    ! groupActions.isDisabledReportActions(state, groupId) &&
    idpStatus &&
    ! IndividualDevelopmentPlanDetailView.isInEditMode(ownProps) &&
    (idpStatus.toLowerCase() === IdpStatus.INCOMPLETE.toLowerCase() ||
        idpStatus.toLowerCase() === IdpStatus.READY.toLowerCase() ||
        idpStatus.toLowerCase() === IdpStatus.REJECTED.toLowerCase());
  const reportName = idpAccessors.getReportName(state, groupIdpId);

  return {
    reportName,
    mode,
    languageCode,
    idpParticipation,
    studentInfo,
    reportCardInfo,
    suggestionsInfo,
    idpId,
    groupIdpId,
    groupId,
    isFromOdin,
    canEdit,
    shouldShowSuggestions,
    skillRecommendations,
    isAnyApiCallSaving: !! isAnyApiCallSaving(state),
    groupStartDate,
    groupEndDate,
    timeZone,
    idpStatus,
    dosRejectedComment,
    isSSV3Group: !! groupActions.isSSV3Group(state, groupId),
    isSkillSuggestionManualOnly: !! groupActions.isFRV2CourseTypeGroup(state, groupId),
  };
}

function mapDispatchToProps(dispatch) {
  return Object.assign(
    { pushRoute: (path) => dispatch(push(path)) },
    bindActionCreators(individualDevelopmentPlans, dispatch));
}

function isAnyApiCallSaving(state) {
  return !! (state.get('apiCalls') || new List())
    .filter((call, key) =>
      ['PUT', 'POST'].some((method) => key.startsWith(method)))
    .find((call) => ApiCall.State.isLoading(call));
}

export default connect(mapStateToProps, mapDispatchToProps)(
  withRouter(IndividualDevelopmentPlanDetailView));



// WEBPACK FOOTER //
// ./containers/Group/IndividualDevelopmentPlan/IndividualDevelopmentPlanDetailView/component.jsx